import { v4 as uuid } from 'uuid';

import type { CreatePartnerBody } from '~/services/LuxLoyalty';

export type PartnerTypeOption = CreatePartnerBody['partnerType'];

/** Keyed by the contract's enum, so a partner type added to the service fails the type check here. */
export const PARTNER_TYPE_LABELS: Record<PartnerTypeOption, string> = {
  TRANSFER: 'Transfer',
  EARN: 'Earn',
  BONUS: 'Bonus',
  OTHER: 'Other',
};

type LimitField =
  | 'minPointsPerTransaction'
  | 'maxPointsPerTransaction'
  | 'minStatusCreditsPerTransaction'
  | 'maxStatusCreditsPerTransaction'
  | 'minBonusPointsPerTransaction'
  | 'maxBonusPointsPerTransaction'
  | 'dailyPointsLimit'
  | 'monthlyPointsLimit'
  | 'maxRequestsPerMinute'
  | 'maxRequestsPerHour';

type FlagField =
  | 'allowCreditPoints'
  | 'allowCreditStatusCredits'
  | 'allowCreditBonusPoints'
  | 'allowDebitPoints'
  | 'allowDebitStatusCredits'
  | 'allowDebitBonusPoints'
  | 'allowRefund'
  | 'allowTierUpgrade'
  | 'requireNameMatch'
  | 'failFast';

interface LimitFieldConfig {
  field: LimitField;
  label: string;
  /** Lowest value the service accepts. */
  min: number;
  /** For a max limit, the min it must not fall below. */
  minField?: LimitField;
  /**
   * For a max limit, the value the service stores when it is left blank. A min is checked against
   * it, since a blank max does not mean "no max". Mirrors the column default in svc-lux-loyalty.
   */
  serviceDefault?: number;
}

/** Integer limits, in render order. Each is optional; a blank one takes the service default. */
export const LIMIT_FIELDS: Array<LimitFieldConfig> = [
  { field: 'minPointsPerTransaction', label: 'Min points / transaction', min: 0 },
  {
    field: 'maxPointsPerTransaction',
    label: 'Max points / transaction',
    min: 1,
    minField: 'minPointsPerTransaction',
    serviceDefault: 1_000_000,
  },
  { field: 'minStatusCreditsPerTransaction', label: 'Min status credits / transaction', min: 1 },
  {
    field: 'maxStatusCreditsPerTransaction',
    label: 'Max status credits / transaction',
    min: 1,
    minField: 'minStatusCreditsPerTransaction',
    serviceDefault: 1_000,
  },
  { field: 'minBonusPointsPerTransaction', label: 'Min bonus points / transaction', min: 1 },
  {
    field: 'maxBonusPointsPerTransaction',
    label: 'Max bonus points / transaction',
    min: 1,
    minField: 'minBonusPointsPerTransaction',
    serviceDefault: 1_000_000,
  },
  { field: 'dailyPointsLimit', label: 'Daily points limit', min: 1 },
  { field: 'monthlyPointsLimit', label: 'Monthly points limit', min: 1 },
  { field: 'maxRequestsPerMinute', label: 'Max requests / minute', min: 1 },
  { field: 'maxRequestsPerHour', label: 'Max requests / hour', min: 1 },
];

/** Permission and behaviour flags, in render order. */
export const FLAG_FIELDS: Array<{ field: FlagField; label: string }> = [
  { field: 'allowCreditPoints', label: 'Credit points' },
  { field: 'allowCreditStatusCredits', label: 'Credit status credits' },
  { field: 'allowCreditBonusPoints', label: 'Credit bonus points' },
  { field: 'allowDebitPoints', label: 'Debit points' },
  { field: 'allowDebitStatusCredits', label: 'Debit status credits' },
  { field: 'allowDebitBonusPoints', label: 'Debit bonus points' },
  { field: 'allowRefund', label: 'Refund' },
  { field: 'allowTierUpgrade', label: 'Tier upgrade' },
  { field: 'requireNameMatch', label: 'Require name match' },
  { field: 'failFast', label: 'Fail fast' },
];

/**
 * The limit columns are Postgres INTEGER. The service does not cap them, so a larger value
 * reaches the database and comes back as a 500.
 */
export const LIMIT_MAX_VALUE = 2_147_483_647;

export const PARTNER_NAME_MAX_LENGTH = 100;
export const LEGAL_NAME_MAX_LENGTH = 255;
/** The `webhook_url` column is VARCHAR(300); the service does not check the length itself. */
export const WEBHOOK_URL_MAX_LENGTH = 300;

/** Numbers are held as strings so a blank input can mean "use the service default". */
export type CreatePartnerFormValues = {
  name: string;
  partnerType: PartnerTypeOption | '';
  webhookUrl: string;
  legalName: string;
  address: string;
  gstPercentage: string;
} & Record<LimitField, string> &
  Record<FlagField, boolean>;

/**
 * Flags are seeded with the service's defaults and always sent, so what the admin sees ticked is
 * what gets saved.
 */
export const CREATE_PARTNER_DEFAULT_VALUES: CreatePartnerFormValues = {
  name: '',
  partnerType: '',
  webhookUrl: '',
  legalName: '',
  address: '',
  gstPercentage: '',
  minPointsPerTransaction: '',
  maxPointsPerTransaction: '',
  minStatusCreditsPerTransaction: '',
  maxStatusCreditsPerTransaction: '',
  minBonusPointsPerTransaction: '',
  maxBonusPointsPerTransaction: '',
  dailyPointsLimit: '',
  monthlyPointsLimit: '',
  maxRequestsPerMinute: '',
  maxRequestsPerHour: '',
  allowCreditPoints: true,
  allowCreditStatusCredits: false,
  allowCreditBonusPoints: false,
  allowDebitPoints: false,
  allowDebitStatusCredits: false,
  allowDebitBonusPoints: false,
  allowRefund: false,
  allowTierUpgrade: false,
  requireNameMatch: false,
  failFast: true,
};

export const PLACEHOLDER_API_KEY_ID_PREFIX = 'placeholder-';

/**
 * A stand-in for the partner's `apiKeyId`.
 *
 * The service requires `apiKeyId`: the AWS API Gateway key ID that the gateway forwards as
 * `x-api-key-id` to identify the partner on partner-facing calls. The gateway key is provisioned
 * separately, so the partner is created with a placeholder and the real key ID is set afterwards.
 * No endpoint can change `apiKeyId`, so that swap is a database update.
 *
 * Random rather than derived from the name: the column is unique (a repeat is a 409), and the
 * value authenticates partner calls, so it must not be guessable. The result is 48 characters,
 * inside the column's 50.
 */
export function buildPlaceholderApiKeyId(): string {
  return `${PLACEHOLDER_API_KEY_ID_PREFIX}${uuid()}`;
}

/**
 * The other half of a min/max limit pair, if the field has one. Each half's validity depends on
 * the other, so editing one side should re-check both.
 */
export function pairedLimitField(field: string): LimitField | undefined {
  const asMax = LIMIT_FIELDS.find((config) => config.field === field)?.minField;
  return asMax ?? LIMIT_FIELDS.find((config) => config.minField === field)?.field;
}

/** Validates one limit field against the whole form, for its min/max pairing. */
export function validateLimit(config: LimitFieldConfig, values: CreatePartnerFormValues): true | string {
  const raw = values[config.field].trim();
  if (raw === '') {
    return true;
  }

  const value = Number(raw);
  if (!Number.isInteger(value)) {
    return 'Enter a whole number';
  }
  if (value < config.min) {
    return `Must be at least ${config.min}`;
  }
  if (value > LIMIT_MAX_VALUE) {
    return `Must be at most ${LIMIT_MAX_VALUE.toLocaleString('en-AU')}`;
  }

  // The service does not compare the pair itself; for points the database does, and rejects the
  // row with a 500 rather than a validation error. Only the max reports a conflict between two
  // filled values, so the pair never shows the same error twice.
  const minRaw = config.minField ? values[config.minField].trim() : '';
  if (minRaw !== '' && Number(minRaw) > value) {
    return 'Must not be below the minimum';
  }

  const maxConfig = LIMIT_FIELDS.find((other) => other.minField === config.field);
  if (maxConfig?.serviceDefault !== undefined && values[maxConfig.field].trim() === '') {
    if (value > maxConfig.serviceDefault) {
      return `Must not be above the default max of ${maxConfig.serviceDefault.toLocaleString('en-AU')}; set a higher max`;
    }
  }

  return true;
}

/**
 * Partner name: required, and short enough for `partner_transaction.source`.
 *
 * Also unique, compared case-insensitively. The service does not enforce it, but partners must send
 * the exact name on every call, and the Medibank SFTP task looks its partner up by name.
 */
export function validatePartnerName(raw: string, existingNames: Array<string>): true | string {
  const name = raw.trim();
  if (name === '') {
    return 'Enter a name';
  }
  if (name.length > PARTNER_NAME_MAX_LENGTH) {
    return `Must be at most ${PARTNER_NAME_MAX_LENGTH} characters`;
  }
  const lowerName = name.toLowerCase();
  if (existingNames.some((existing) => existing.trim().toLowerCase() === lowerName)) {
    return 'A partner with this name already exists';
  }
  return true;
}

/** Optional URL: blank is fine, anything else must be http(s). */
export function validateWebhookUrl(raw: string): true | string {
  const value = raw.trim();
  if (value === '') {
    return true;
  }
  if (value.length > WEBHOOK_URL_MAX_LENGTH) {
    return `Must be at most ${WEBHOOK_URL_MAX_LENGTH} characters`;
  }

  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:' ? true : 'Enter an http(s) URL';
  } catch {
    return 'Enter a valid URL';
  }
}

/** Optional percentage: 0–100, at most two decimal places, as the service requires. */
export function validateGstPercentage(raw: string): true | string {
  const value = raw.trim();
  if (value === '') {
    return true;
  }
  // A regex rather than arithmetic: 0.07 * 100 is not an integer in floating point.
  if (!/^\d+(\.\d{1,2})?$/.test(value) || Number(value) > 100) {
    return 'Enter a percentage between 0 and 100, up to two decimal places';
  }
  return true;
}

/**
 * Builds the create body from validated form values.
 *
 * Blank optional fields are omitted rather than sent empty: the service reads a missing field as
 * "use the default" and accepts no nulls.
 */
export function toCreatePartnerBody(values: CreatePartnerFormValues, apiKeyId: string): CreatePartnerBody {
  const body: CreatePartnerBody = {
    name: values.name.trim(),
    apiKeyId,
    // Required by the form, so never blank by the time the values are valid.
    partnerType: values.partnerType as PartnerTypeOption,
  };

  for (const { field } of LIMIT_FIELDS) {
    const raw = values[field].trim();
    if (raw !== '') {
      body[field] = Number(raw);
    }
  }

  for (const { field } of FLAG_FIELDS) {
    body[field] = values[field];
  }

  const webhookUrl = values.webhookUrl.trim();
  const legalName = values.legalName.trim();
  const address = values.address.trim();
  const gstPercentage = values.gstPercentage.trim();

  return {
    ...body,
    ...(webhookUrl && { webhookUrl }),
    ...(legalName && { legalName }),
    ...(address && { address }),
    ...(gstPercentage && { gstPercentage: Number(gstPercentage) }),
  };
}
